import { getCookie } from '@jouzen/ecom-utils';
import type { MiddlewareFunction } from '@segment/analytics-next';

import { Partner } from '@/configs/partner';
import { debugAnalytics } from '@/utils/debugAnalytics';

import { getAnonymousId } from '../getAnonymousId';
import { isHipaa } from '../isHipaa';

const MASKED_IP = '0.0.0.0';
const STATSIG_STABLE_ID_COOKIE = 'statsigStableId';

/**
 * Derives the current partner from the subdomain of the hostname,
 * mirroring the logic in src/middleware.ts.
 * e.g. essence.ouraring.com -> Partner.essence
 */
function getPartner(): Partner | undefined {
  const subdomain = globalThis.location?.hostname.split('.')[0] ?? '';
  return Object.values(Partner).find((p) => p === subdomain);
}

/**
 * Source middleware that enriches every Segment event with HIPAA-required
 * fields, drops events where a unique anonymousId cannot be guaranteed, and
 * blocks `group` / `alias` events outright.
 *
 * Implemented as a source middleware (not an AJS2 plugin) to work around a
 * deadlock in `@jouzen/onetrust-consent-manager` <= 2.6.0: passing this via
 * the `segmentPlugins` prop causes the wrapper to `await a.register(...)`
 * while the AnalyticsBrowser is still pre-init, which buffers the call into
 * `_preInitBuffer` and never resolves because `a.load(...)` is queued
 * AFTER the awaited register on the same line. See CSOL-187.
 *
 * Replaces the full sourceMiddlewares pipeline from pre-OneTrust:
 * - withAnonymousId: sets app-scoped anonymousId from localStorage
 * - withCheckAnonymousId: drops events where anonymousId collides with D2C cookie
 * - withEnhancedPayload: stamps isHipaa, masks ip, scrubs userId, adds statsigStableId
 * - withCheckStatsigId: warns when statsigStableId is missing
 * - withEventTypes: drops group / alias events
 */
export const hipaaMiddleware: MiddlewareFunction = ({ payload, next }) => {
  const event = payload.obj;

  // --- withEventTypes ---
  // Drop `group` and `alias` events outright. Source middleware drops via
  // `next(null)` (vs. the plugin model's `throw`).
  if (event.type === 'group' || event.type === 'alias') {
    debugAnalytics(`hipaaMiddleware: dropping ${event.type} event`, {
      data: { type: event.type },
      level: 'warn',
      logToDatadog: 'hipaaMiddleware',
    });
    next(null);
    return;
  }

  const partner = getPartner();
  const hipaa = isHipaa(partner);

  // --- withAnonymousId + withCheckAnonymousId ---
  // getAnonymousId returns null if a unique ID cannot be generated after
  // MAX_RETRY_ATTEMPTS, meaning it still collides with the D2C Segment cookie.
  // Drop the event rather than risk leaking a cross-context identifier.
  const anonymousId = getAnonymousId();
  if (!anonymousId) {
    debugAnalytics(
      'hipaaMiddleware: dropping event — could not generate a unique anonymousId',
      {
        data: { type: event.type },
        level: 'error',
        logToDatadog: 'hipaaMiddleware',
      },
    );
    next(null);
    return;
  }

  // --- withCheckStatsigId (warn only, not blocking) ---
  const statsigStableId = getCookie(STATSIG_STABLE_ID_COOKIE);
  if (!statsigStableId) {
    debugAnalytics('hipaaMiddleware: statsigStableId cookie is missing', {
      data: { partner },
      level: 'warn',
      logToDatadog: 'hipaaMiddleware',
    });
  }

  // --- withEnhancedPayload ---
  // Stamp isHipaa on context.traits and properties.
  // isHipaa is true only for Partner.essence; false for all other subdomains.
  event.anonymousId = anonymousId;
  event.context = {
    ...event.context,
    ip: MASKED_IP,
    traits: {
      ...event.context?.traits,
      isHipaa: hipaa,
    },
  };
  event.properties = {
    ...event.properties,
    isHipaa: hipaa,
    statsigStableId: statsigStableId ?? undefined,
  };

  // Remove userId — eop events must be anonymous.
  event.userId = undefined;

  next(payload);
};
